/**
 * A route's display name, its grade range, and the default labels behind them
 * — one copy for the app and for the recap / Wrapped e-mails.
 *
 * A nameless route is shown as "{hold colour} {difficulty}". The app resolves
 * it through i18n, so an English user reads "Blue Red"; the e-mails used to
 * carry private copies that printed the gym's raw labels, so the same English
 * user's e-mail said "Kék Piros", and the Wrapped joined a grade range with a
 * bare dash and fell back to the V-scale where the app never does. The rule
 * now lives here once, and the LABELS are a parameter: the client hands in a
 * table built from `t`, the e-mails the recipient's language table — both of
 * which spread `ROUTE_LABELS` below, so the default labels cannot diverge.
 */
import { GRADE_SCALE } from "./gradeScale.js";
import { getDifficultyConfig } from "./difficultyResolution.js";

/* ── The default labels, both languages ──────────────────────────────────── */

/** The built-in difficulty keys whose labels are translated; a custom
 *  (admin-added) level keeps its own stored label. */
export const DEFAULT_DIFF_KEYS = ["white", "blue", "yellow", "green", "red", "black"];

/** The built-in hold-colour keys whose labels are translated. */
export const DEFAULT_HOLD_KEYS = ["pink", "orange", "yellow", "green", "blue", "red", "purple", "white", "black"];

/**
 * The default labels per language. BOTH dictionaries read these:
 *   - the client `i18n/hu.js` / `en.js` spread `routeLabelDictEntries(ROUTE_LABELS.hu)`
 *     into their flat `"diff.white": …` / `"hold.pink": …` / `"route.unknown…": …` keys;
 *   - the e-mail `emails/i18n/hu.js` / `en.js` spread `...ROUTE_LABELS.hu` as their
 *     `diff` / `hold` / `unknownColor` / `unknownDifficulty` entries.
 * `t()` keeps reading the dictionary; only the source of these strings moved.
 */
export const ROUTE_LABELS = {
  hu: {
    diff: { white: "Fehér", blue: "Kék", yellow: "Sárga", green: "Zöld", red: "Piros", black: "Fekete" },
    hold: { pink: "Rózsaszín", orange: "Narancs", yellow: "Sárga", green: "Zöld", blue: "Kék", red: "Piros", purple: "Lila", white: "Fehér", black: "Fekete" },
    unknownColor: "Ismeretlen szín",
    unknownDifficulty: "Ismeretlen nehézség",
  },
  en: {
    diff: { white: "White", blue: "Blue", yellow: "Yellow", green: "Green", red: "Red", black: "Black" },
    hold: { pink: "Pink", orange: "Orange", yellow: "Yellow", green: "Green", blue: "Blue", red: "Red", purple: "Purple", white: "White", black: "Black" },
    unknownColor: "Unknown color",
    unknownDifficulty: "Unknown difficulty",
  },
};

/**
 * One language's labels as the client dictionary's flat keys:
 * `diff.<key>`, `hold.<key>`, `route.unknownColor`, `route.unknownDifficulty`.
 */
export function routeLabelDictEntries(labels) {
  const out = {};
  for (const [key, label] of Object.entries(labels.diff)) out[`diff.${key}`] = label;
  for (const [key, label] of Object.entries(labels.hold)) out[`hold.${key}`] = label;
  out["route.unknownColor"] = labels.unknownColor;
  out["route.unknownDifficulty"] = labels.unknownDifficulty;
  return out;
}

/* ── Label lookup ────────────────────────────────────────────────────────── */

/** Translate a default difficulty label; a custom (admin-added) level keeps its own. */
export function defaultDiffLabel(labels, key, fallbackLabel) {
  if (DEFAULT_DIFF_KEYS.includes(key) && labels && labels.diff && labels.diff[key]) return labels.diff[key];
  return fallbackLabel;
}

/** Translate a default hold colour label; a custom colour keeps its own. */
export function defaultHoldLabel(labels, key, fallbackLabel) {
  if (DEFAULT_HOLD_KEYS.includes(key) && labels && labels.hold && labels.hold[key]) return labels.hold[key];
  return fallbackLabel;
}

/* ── The name ────────────────────────────────────────────────────────────── */

/**
 * The name the app shows for a route: its own name when it has one, else
 * "{hold colour} {difficulty}" — with the "unknown" placeholders when a part
 * is missing, so the app and the e-mails never differ.
 *
 * @param {object} route       route document (name, holdColor, difficulty)
 * @param {object} difficulty  the resolved difficulty set for the route's discipline
 *                             (keys → { label, … })
 * @param {object} holdColors  the gym's hold colour set (keys → { label, … })
 * @param {object} labels      `{ diff, hold, unknownColor, unknownDifficulty }` in
 *                             the viewer's language (the shape of `ROUTE_LABELS.hu`)
 * @returns {string}
 */
export function routeDisplayName(route, difficulty, holdColors, labels) {
  if (route && route.name && route.name.trim() !== "") return route.name;
  const diffSet = difficulty || {};
  const holdSet = holdColors || {};
  const diffEntry = route && route.difficulty != null ? (diffSet[route.difficulty] || null) : null;
  const holdEntry = route && route.holdColor != null ? (holdSet[route.holdColor] || null) : null;
  const colorLabel = holdEntry
    ? defaultHoldLabel(labels, route.holdColor, holdEntry.label)
    : (labels && labels.unknownColor) || "";
  const diffLabel = diffEntry
    ? defaultDiffLabel(labels, route.difficulty, diffEntry.label)
    : (labels && labels.unknownDifficulty) || "";
  return `${colorLabel} ${diffLabel}`;
}

/**
 * `routeDisplayName` from a whole gym document — the server side, which holds
 * the raw gym doc rather than the client's resolved sets. The difficulty set is
 * the route's discipline's (a mixed gym prices lead and boulder separately),
 * the hold colours are the gym's own. Written once because the recap, the
 * Wrapped and the watched-route push all need exactly this composition.
 *
 * @param {object} route  route document (name, holdColor, difficulty, discipline)
 * @param {object} gym    gym document (settings, disciplines)
 * @param {object} labels the viewer's labels (the shape of `ROUTE_LABELS.hu`)
 * @returns {string}
 */
export function routeDisplayNameForGym(route, gym, labels) {
  const holdColors = gym && gym.settings ? gym.settings.customHoldColors : undefined;
  return routeDisplayName(route, getDifficultyConfig(gym, route && route.discipline), holdColors, labels);
}

/* ── The grade range ─────────────────────────────────────────────────────── */

/**
 * Format a gradeRange as a display string.
 * Single grade: "6a" / "V4". Range: "6a – 6c" / "V3+ – V5+" — the app's spaced
 * dash, the same field preference everywhere.
 * @param {object} gradeRange - { min, max } gradeId pair
 * @param {string} preference - "font" | "v" | "french" | "uiaa" (anything else → font)
 * @param {Array} [gradeScale] - optional custom grade scale array
 * @returns {string|null} formatted string, or null if there is no range or the
 *   scale does not know an end of it — the caller decides the fallback
 */
export function formatGradeRange(gradeRange, preference = "font", gradeScale) {
  if (!gradeRange) return null;
  const scale = gradeScale || GRADE_SCALE;
  const { min, max } = gradeRange;
  const field = preference === "v" ? "v" : preference === "uiaa" ? "uiaa" : preference === "french" ? "french" : "font";
  const minG = scale[min];
  const maxG = scale[max];
  if (!minG || !maxG) return null;
  if (min === max) return minG[field];
  return `${minG[field]} – ${maxG[field]}`;
}
